import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { cartAPI } from '../services/api';
import { Cart, Product } from '../types';
import { useAuth } from './AuthContext';
import { appToast } from '../utils/toast';
import { useLanguage } from './LanguageContext';
import { STORAGE_KEYS } from '../constants';

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  addToCart: (product: Product, quantity: number, selectedImage?: string, variantId?: string) => Promise<void>;
  updateCartItem: (itemId: number, quantity: number) => Promise<void>;
  removeFromCart: (itemId: number) => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

// A guest cart line stores the full product snapshot (already available when
// adding to cart) plus a quantity. We avoid an extra fetch and tolerate
// slightly stale prices until the cart is merged into the account on login.
interface GuestCartItem {
  product: Product;
  quantity: number;
  // The chosen gallery photo (color/variant); undefined means the main image.
  selected_image?: string;
  // The chosen size variant for a grouped product (undefined for plain items).
  variant_id?: string;
  variant_size?: string;
}

// Resolve the chosen size variant on a product (by id), if any.
const findVariant = (product: Product, variantId?: string) =>
  variantId ? (product.variants || []).find((v) => v.variant_id === variantId) : undefined;

// The unit price for a line: the variant's price when a size is chosen,
// otherwise the product's (already discount-applied) price.
const linePrice = (product: Product, variantId?: string): number => {
  const v = findVariant(product, variantId);
  return Number(v ? v.price : product.price) || 0;
};

// The stock ceiling for a line: the variant's stock when a size is chosen.
const lineStock = (product: Product, variantId?: string): number => {
  const v = findVariant(product, variantId);
  return v ? (v.stock ?? 0) : (product.stock ?? 0);
};

// A guest cart line is uniquely identified by its product, the selected photo
// AND the selected size variant, so the same product in two sizes/colors stays
// as separate lines. We derive a stable numeric id (the CartItem.id the Cart
// page keys off) by hashing that composite key.
const guestLineKey = (productId: number, selectedImage?: string, variantId?: string): string =>
  `${productId}::${selectedImage || ''}::${variantId || ''}`;

const hashLineId = (key: string): number => {
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (Math.imul(h, 31) + key.charCodeAt(i)) | 0;
  }
  return Math.abs(h) || 1;
};

const guestItemId = (item: GuestCartItem): number =>
  hashLineId(guestLineKey(item.product.id, item.selected_image, item.variant_id));

const readGuestCart = (): GuestCartItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.GUEST_CART);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeGuestCart = (items: GuestCartItem[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.GUEST_CART, JSON.stringify(items));
  } catch {
    // ignore storage write failures (private mode, quota, etc.)
  }
};

const clearGuestCart = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.GUEST_CART);
  } catch {
    // ignore
  }
};

// Build the server-shaped Cart object from the local guest snapshot. We reuse
// each product's numeric id as the synthetic CartItem id so the Cart page's
// update/remove handlers (which key off item.id) work unchanged.
const buildGuestCart = (items: GuestCartItem[]): Cart => {
  const total = items.reduce(
    (sum, item) => sum + linePrice(item.product, item.variant_id) * item.quantity,
    0
  );
  const item_count = items.reduce((sum, item) => sum + item.quantity, 0);
  return {
    id: 0,
    user: 0,
    items: items.map((item) => ({
      id: guestItemId(item),
      product: item.product,
      quantity: item.quantity,
      price: linePrice(item.product, item.variant_id),
      selected_image: item.selected_image,
      variant_id: item.variant_id,
      variant_size: item.variant_size,
    })),
    total: Math.round(total * 100) / 100,
    item_count,
  };
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();
  // Ensures the guest -> account merge runs at most once per login.
  const mergedRef = useRef(false);

  const loadGuestCart = useCallback((): void => {
    setCart(buildGuestCart(readGuestCart()));
  }, []);

  const refreshCart = useCallback(async (): Promise<void> => {
    if (!user) {
      loadGuestCart();
      return;
    }

    try {
      setLoading(true);
      const response = await cartAPI.getCart();
      setCart(response.data);
    } catch {
      appToast.error(t.toasts.cartLoadFailed);
    } finally {
      setLoading(false);
    }
  }, [user, loadGuestCart]);

  // Merge any guest cart into the account once, right after login, then refresh
  // from the server. Falls back to a plain refresh when there's nothing to merge.
  const mergeGuestCart = useCallback(async (): Promise<void> => {
    const guestItems = readGuestCart();
    if (guestItems.length === 0) {
      await refreshCart();
      return;
    }
    try {
      setLoading(true);
      for (const item of guestItems) {
        try {
          await cartAPI.addToCart({ product: item.product.id, quantity: item.quantity, selected_image: item.selected_image, variant_id: item.variant_id });
        } catch {
          // Skip items that can't be merged (e.g. out of stock) and continue.
        }
      }
    } finally {
      clearGuestCart();
      await refreshCart();
    }
  }, [refreshCart]);

  useEffect(() => {
    if (user) {
      if (!mergedRef.current) {
        mergedRef.current = true;
        mergeGuestCart();
      }
    } else {
      mergedRef.current = false;
      loadGuestCart();
    }
  }, [user, mergeGuestCart, loadGuestCart]);

  const addToCart = async (product: Product, quantity: number, selectedImage?: string, variantId?: string): Promise<void> => {
    if (!user) {
      const items = readGuestCart();
      const matches = (item: GuestCartItem) =>
        item.product.id === product.id
        && (item.selected_image || '') === (selectedImage || '')
        && (item.variant_id || '') === (variantId || '');
      const existing = items.find(matches);
      const currentQty = existing ? existing.quantity : 0;
      const stock = lineStock(product, variantId);

      if (stock > 0 && currentQty + quantity > stock) {
        const message = t.cart.addStockLimit.replace('{count}', String(stock));
        appToast.error(message);
        throw new Error(message);
      }

      const variantSize = findVariant(product, variantId)?.size;
      const nextItems = existing
        ? items.map((item) =>
            matches(item)
              ? { ...item, product, quantity: item.quantity + quantity }
              : item
          )
        : [...items, { product, quantity, selected_image: selectedImage, variant_id: variantId, variant_size: variantSize }];

      writeGuestCart(nextItems);
      setCart(buildGuestCart(nextItems));
      return;
    }

    try {
      setLoading(true);
      await cartAPI.addToCart({ product: product.id, quantity, selected_image: selectedImage, variant_id: variantId });
      await refreshCart();
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.detail || t.toasts.cartAddFailed;
      appToast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId: number, quantity: number): Promise<void> => {
    if (!user) {
      const items = readGuestCart();
      const target = items.find((item) => guestItemId(item) === itemId);
      if (target) {
        const stock = lineStock(target.product, target.variant_id);
        if (stock > 0 && quantity > stock) {
          const message = t.cart.onlyLeftInStock.replace('{count}', String(stock));
          appToast.error(message);
          throw new Error(message);
        }
      }
      const nextItems = items.map((item) =>
        guestItemId(item) === itemId ? { ...item, quantity } : item
      );
      writeGuestCart(nextItems);
      setCart(buildGuestCart(nextItems));
      return;
    }

    try {
      setLoading(true);
      await cartAPI.updateCartItem({ item_id: itemId, quantity });
      await refreshCart();
      appToast.success(t.toasts.cartUpdateSuccess);
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.detail || t.toasts.cartUpdateFailed;
      appToast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId: number): Promise<void> => {
    if (!user) {
      const nextItems = readGuestCart().filter((item) => guestItemId(item) !== itemId);
      writeGuestCart(nextItems);
      setCart(buildGuestCart(nextItems));
      appToast.success(t.toasts.cartRemoveSuccess);
      return;
    }

    try {
      setLoading(true);
      await cartAPI.removeFromCart(itemId);
      await refreshCart();
      appToast.success(t.toasts.cartRemoveSuccess);
    } catch (error: any) {
      const message = error.response?.data?.message || t.toasts.cartRemoveFailed;
      appToast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: CartContextType = {
    cart,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    refreshCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 
